import { useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import axios from "axios";
import { path } from "../../../routes";

const CAMERA_OPTIONS = [
  { id: "cam1", key: "camera1", label: "Camera 1", url: "rtsp://admin:xZSx2026xNbk%21@89.190.32.75:554/stream1" },
  { id: "cam2", key: "camera2", label: "Camera 2", url: "rtsp://admin:xZSx2026xNbk%21@89.190.32.75:554/stream2" },
];

const normalizeRtspUrl = (u) => {
  const s = String(u || "").trim();
  if (!/^rtsp:\/\//i.test(s)) return s;
  const m = s.match(/^rtsp:\/\/([^@\/]+)@(.+)$/i);
  if (!m) return s;
  const userinfo = m[1];
  const hostPath = m[2];
  const idx = userinfo.indexOf(":");
  if (idx < 0) return s;
  const user = userinfo.slice(0, idx);
  const passRaw = userinfo.slice(idx + 1);
  let pass = passRaw;
  try { pass = decodeURIComponent(passRaw); } catch (_) {}
  const enc = (v) => encodeURIComponent(v).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  return `rtsp://${enc(user)}:${enc(pass)}@${hostPath}`;
};

const LiveCameras = ({ cred }) => {
  const [devices, setDevices] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(CAMERA_OPTIONS[0].id);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [cameraUrl, setCameraUrl] = useState(CAMERA_OPTIONS[0].url);

  const cameraById = useMemo(
    () => Object.fromEntries(CAMERA_OPTIONS.map((c) => [c.id, c])),
    []
  );

  const getDeviceSettingsSafe = async (deviceId, orgId) => {
    try {
      const resp = await axios.get(`${path}/getDeviceSettings/${deviceId}?orgID=${encodeURIComponent(orgId)}`);
      return (resp.data?.settings && typeof resp.data.settings === "object") ? resp.data.settings : {};
    } catch (_) {
      return {};
    }
  };

  const fetchDevices = async () => {
    try {
      const orgId = cred?.cOrganization_id;
      const [kiosksRes, displaysRes] = await Promise.all([
        axios.get(`${path}/getDeviceV2?orgID=${orgId}`).catch(() => ({ data: [] })),
        axios.get(`${path}/getDisplaysbyOrg/${orgId}`).catch(() => ({ data: [] })),
      ]);
      const kiosks = Array.isArray(kiosksRes.data) ? kiosksRes.data : [];
      const displays = Array.isArray(displaysRes.data) ? displaysRes.data : [];
      const seen = new Set();
      const all = [...displays, ...kiosks].filter((d) => {
        const id = Number(d?.id);
        if (!Number.isFinite(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      setDevices(all);

      // Load persisted assignment so checkboxes/camera selection stay after refresh.
      setLoadingAssignments(true);
      const selected = [];
      let selectedCameraFromSettings = null;
      for (const d of all) {
        const s = await getDeviceSettingsSafe(d.id, orgId);
        if (s?.liveCameraEnabled) {
          selected.push(d.id);
          if (!selectedCameraFromSettings && typeof s?.liveCameraKey === "string" && s.liveCameraKey.trim()) {
            selectedCameraFromSettings = s.liveCameraKey.trim();
          }
        }
      }
      setSelectedDeviceIds(selected);
      if (selectedCameraFromSettings) {
        const mapped = CAMERA_OPTIONS.find((c) => c.key === selectedCameraFromSettings);
        if (mapped) setSelectedCamera(mapped.id);
      }
      // If any selected device has custom URL, keep it visible in input.
      for (const d of all) {
        const s = await getDeviceSettingsSafe(d.id, orgId);
        if (s?.liveCameraEnabled && typeof s?.liveCameraUrl === "string" && s.liveCameraUrl.trim()) {
          setCameraUrl(s.liveCameraUrl.trim());
          break;
        }
      }
    } catch (e) {
      console.error("Failed to load devices for live cameras:", e);
      setDevices([]);
      setSelectedDeviceIds([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cred?.cOrganization_id]);

  const toggleDevice = (id) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    const camera = cameraById[selectedCamera];
    if (camera) setCameraUrl(camera.url);
  }, [selectedCamera, cameraById]);

  const save = async () => {
    const camera = cameraById[selectedCamera];
    if (!camera) return;
    setSaving(true);
    try {
      const orgId = cred?.cOrganization_id;
      const targetUrl = normalizeRtspUrl(String(cameraUrl || camera.url).trim());
      const isRtsp = /^rtsp:\/\//i.test(targetUrl);
      // Save sequentially to avoid overloading backend (large Promise.all caused freezes).
      for (const d of devices) {
        const isSelected = selectedDeviceIds.includes(d.id);
        const current = await getDeviceSettingsSafe(d.id, orgId);
        const wasEnabled = Boolean(current?.liveCameraEnabled);
        const hadCameraData = Boolean(current?.liveCameraUrl || current?.liveCameraKey);
        // Skip untouched devices for faster propagation to selected kiosk(s).
        if (!isSelected && !wasEnabled && !hadCameraData) continue;
        const next = {
          ...current,
          liveCameraEnabled: Boolean(isSelected),
          liveCameraUrl: isSelected ? targetUrl : null,
          liveCameraName: isSelected ? camera.label : null,
          liveCameraKey: (isSelected && isRtsp) ? camera.key : null,
        };
        await axios.put(`${path}/setDeviceSettings/${d.id}`, { settings: next, orgID: orgId });
      }

      // For RTSP source, ask backend to (re)start live bridge so kiosk gets fresh live stream.
      if (isRtsp && selectedDeviceIds.length > 0) {
        await axios.post(`${path}/live-camera/start`, { cameraKey: camera.key, rtspUrl: targetUrl }).catch(() => {});
      }
      alert("Live camera assignment saved.");
    } catch (e) {
      console.error("Failed to save live camera assignment:", e);
      alert("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const disableAll = async () => {
    setSaving(true);
    try {
      const orgId = cred?.cOrganization_id;
      for (const d of devices) {
        const current = await getDeviceSettingsSafe(d.id, orgId);
        const next = {
          ...current,
          liveCameraEnabled: false,
          liveCameraUrl: null,
          liveCameraName: null,
          liveCameraKey: null,
        };
        await axios.put(`${path}/setDeviceSettings/${d.id}`, { settings: next, orgID: orgId });
      }
      await Promise.allSettled([
        axios.post(`${path}/live-camera/stop`, { cameraKey: "camera1" }),
        axios.post(`${path}/live-camera/stop`, { cameraKey: "camera2" }),
      ]);
      setSelectedDeviceIds([]);
      alert("Live cameras disabled on all devices.");
    } catch (e) {
      console.error("Failed to disable live cameras:", e);
      alert("Disable failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 2 },
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
        Live Cameras
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "#666", lineHeight: 1.5 }}>
        Select camera and devices where live stream should be shown. Save to activate, Disable All to stop.
      </Typography>
      <FormControl fullWidth sx={{ mb: 2, minWidth: 0, maxWidth: { xs: "100%", sm: 480, md: 560 } }}>
        <InputLabel id="live-camera-label">Camera</InputLabel>
        <Select
          labelId="live-camera-label"
          label="Camera"
          value={selectedCamera}
          onChange={(e) => setSelectedCamera(e.target.value)}
          renderValue={(id) => {
            const c = cameraById[id];
            return c ? c.label : "";
          }}
          MenuProps={{
            PaperProps: {
              sx: { maxWidth: "calc(100vw - 32px)" },
            },
          }}
        >
          {CAMERA_OPTIONS.map((c) => (
            <MenuItem
              key={c.id}
              value={c.id}
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                alignItems: "flex-start",
                py: 1,
              }}
            >
              <Typography component="span" variant="body2" sx={{ display: "block" }}>
                <strong>{c.label}</strong>
                <br />
                <span style={{ opacity: 0.85 }}>{c.url}</span>
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        label="Stream URL (HLS/WebRTC preferred)"
        value={cameraUrl}
        onChange={(e) => setCameraUrl(e.target.value)}
        sx={{
          mb: 2,
          maxWidth: { xs: "100%", md: 900 },
          "& .MuiInputBase-input": {
            fontSize: { xs: "0.875rem", sm: "1rem" },
            wordBreak: "break-all",
          },
        }}
        multiline
        minRows={2}
        helperText="Use external URL (e.g. https://.../index.m3u8). RTSP works only with server transcoding enabled."
      />
      <Box
        sx={{
          maxHeight: { xs: 280, sm: 420 },
          overflow: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: { xs: 1, sm: 1.5 },
          mb: 2,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {loadingAssignments ? (
          <Typography variant="body2" color="text.secondary">Loading saved state...</Typography>
        ) : devices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No devices.</Typography>
        ) : (
          devices.map((d) => (
            <FormControlLabel
              key={d.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                ml: 0,
                mr: 0,
                mb: 0.5,
                "& .MuiCheckbox-root": { pt: 0.25 },
              }}
              control={<Checkbox checked={selectedDeviceIds.includes(d.id)} onChange={() => toggleDevice(d.id)} />}
              label={
                <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>
                  {`${d.description || `Device ${d.id}`} (${Number(d.device_group) === 2 ? "Kiosk" : "Display"})`}
                </Typography>
              }
            />
          ))
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.5 },
          "& .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: 44, sm: 36 },
          },
        }}
      >
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button variant="outlined" color="error" onClick={disableAll} disabled={saving}>
          Disable All
        </Button>
      </Box>
    </Box>
  );
};

export default LiveCameras;
